import React, { useState, useEffect, useRef, useCallback } from 'react';
import { ListBox, ListBoxChangeEvent } from 'primereact/listbox';
import { createRoot, Root } from 'react-dom/client';
import { SphereAvatar } from '../../../design-system-my-lib';
import { GlyphColor } from '../../../types/glyph';
import './listbox.scss';

// Each item has a name and a code, just like your Option interface
interface Option {
  name: string;
  code: string;
  glyph?: string;
  glyphColor?: GlyphColor;
  imageId?: string;
}

interface ListBoxComponentProps {
  /** The array of selectable items, each with `name` (label) and `code` (value). */
  options: Option[];

  /** Optional placeholder or label displayed when no item is selected. */
  placeholder?: string;

  /** If you need to respond when user selects/unselects. */
  onSelectionChange?: (selectedOptions: Option[] | null) => void;

  /** Custom CSS class for styling. */
  className?: string;

  /** If true, enable client-side filtering in the listbox. Defaults to true. */
  filter?: boolean;

  /** Callback to notify parent about toggle changes. */
  onNearbyModeToggled?: (e: { nearbyMode: boolean }) => void;

  /** When true, a toggle button for nearby mode is rendered. */
  showNearbyToggle?: boolean;

  /** When false, only one item can be selected. Defaults to true. */
  allowMultiple?: boolean;
}

/**
 * ListBoxComponent: a prime-react ListBox that mimics your MultiSelect logic,
 * but is always open and visible, letting users pick multiple items.
 */
const ListBoxComponent: React.FC<ListBoxComponentProps> = ({
  options,
  placeholder = 'Select Options',
  onSelectionChange,
  className = '',
  filter = true,
  onNearbyModeToggled,
  showNearbyToggle = false,
  allowMultiple = true,
}) => {
  // Track the currently selected items (Option[]) in local state.
  const [selectedOptions, setSelectedOptions] = useState<Option[] | null>(null);
  // Track the nearby mode.
  const [nearbyMode, setNearbyMode] = useState(false);

  // Called by PrimeReact's ListBox whenever selection changes.
  const handleChange = (e: ListBoxChangeEvent) => {
    setSelectedOptions(e.value);
    onSelectionChange?.(e.value);
  };

  // Template for rendering each option.
  const itemTemplate = (option: Option) => (
    <div className="listbox-item">
      <SphereAvatar
        sphereId={option.code}
        glyph={option.glyph}
        glyphColor={option.glyphColor}
        imageId={option.imageId}
        decorative
      />
      <span className="listbox-item-text">{option.name}</span>
    </div>
  );

  // Toggle function to flip nearby mode.
  const toggleIcon = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    setNearbyMode((prev) => !prev);
  }, []);

  // Notify parent when nearbyMode changes.
  useEffect(() => {
    if (onNearbyModeToggled) {
      onNearbyModeToggled({ nearbyMode });
    }
  }, [nearbyMode, onNearbyModeToggled]);

  // Inject the toggle button into the filter container if showNearbyToggle is true.
  const toggleButtonRootRef = useRef<Root | null>(null);
  useEffect(() => {
    if (!showNearbyToggle || !filter) return;
    // Adjust the selector if your ListBox filter container has a different class.
    const filterContainer = document.querySelector(
      '.p-listbox-filter-container'
    );
    if (filterContainer) {
      let toggleWrapper = filterContainer.querySelector(
        '.toggle-button-container'
      ) as HTMLDivElement;
      if (!toggleWrapper) {
        toggleWrapper = document.createElement('div');
        toggleWrapper.className = 'toggle-button-container';
        // Prevent clicks from propagating (which might trigger filter actions).
        toggleWrapper.addEventListener('mousedown', (e) => e.stopPropagation());
        filterContainer.appendChild(toggleWrapper);
      }
      if (!toggleButtonRootRef.current) {
        toggleButtonRootRef.current = createRoot(toggleWrapper);
      }
      toggleButtonRootRef.current.render(
        <button
          className={`toggle-icon ${
            nearbyMode ? 'nearby-mode-icon' : 'everywhere-mode-icon'
          }`}
          onClick={toggleIcon}
          onMouseDown={(e) => e.preventDefault()}
        />
      );
    }
  }, [showNearbyToggle, filter, nearbyMode, toggleIcon]);

  return (
    <ListBox
      value={selectedOptions}
      onChange={handleChange}
      options={options}
      multiple={allowMultiple}
      optionLabel="name"
      filter={filter}
      placeholder={placeholder}
      className={className}
      listStyle={{ maxHeight: '250px' }}
      itemTemplate={itemTemplate}
    />
  );
};

export default ListBoxComponent;
